import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, ChevronRight, Star, Zap, Shield, MapPin, Clock, Globe,
  CarTaxiFront, Package, Truck, Car, Wrench, CircleDot, Droplets, Bus,
} from "lucide-react";

/**
 * Marketing Landing — the public face of drivon.co.il / drivon.app.
 * Kept isolated from the in-app `/` landing so we can point the marketing
 * domain here without dragging the auth/onboarding UI along.
 *
 * Store-badge URLs are placeholders — swap `PLAY_STORE_URL` and
 * `APP_STORE_URL` when Google Play / App Store links are live.
 */

// TODO(swap-when-live): replace both consts with the real listing URLs.
const PLAY_STORE_URL = "#play-store-coming-soon";
const APP_STORE_URL  = "#app-store-coming-soon";

const CATEGORIES = [
  { id: "taxi",     icon: CarTaxiFront },
  { id: "delivery", icon: Package },
  { id: "moving",   icon: Truck },
  { id: "towing",   icon: Car },
  { id: "mechanic", icon: Wrench },
  { id: "tire",     icon: CircleDot },
  { id: "carwash",  icon: Droplets },
  { id: "bus",      icon: Bus },
];

const FEATURES = [
  { icon: Zap,    kKey: "feat1t", vKey: "feat1v" },
  { icon: MapPin, kKey: "feat2t", vKey: "feat2v" },
  { icon: Shield, kKey: "feat3t", vKey: "feat3v" },
  { icon: Globe,  kKey: "feat4t", vKey: "feat4v" },
];

export default function MarketingLanding() {
  const { t } = useTranslation();
  return (
    <div className="bg-ink-bg min-h-screen text-white" data-testid="marketing-landing">
      <MarketingNav t={t} />
      <Hero t={t} />
      <ServicesStrip t={t} />
      <RidersDrivers t={t} />
      <Features t={t} />
      <DownloadCta t={t} />
      <MarketingFooter t={t} />
      <MobileStickyCta t={t} />
    </div>
  );
}

/** Mobile-only sticky CTA. Sits above the bottom viewport edge on small
 * screens so the "Get the app" action is always one tap away. Hidden on
 * md+ where the sticky top nav is roomier. */
function MobileStickyCta({ t }) {
  return (
    <div className="md:hidden fixed inset-x-0 bottom-0 z-50 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3 bg-gradient-to-t from-ink-bg via-ink-bg/95 to-transparent" data-testid="mk-sticky-cta">
      <a
        href="#download"
        className="pill-btn w-full flex items-center justify-center gap-2 bg-white text-black rounded-full py-3.5 text-sm font-bold shadow-2xl"
        data-testid="mk-sticky-cta-btn"
      >
        {t("mk.hero.ctaGet", "Get the app")} <ArrowRight size={16} className="rtl-flip" />
      </a>
    </div>
  );
}

function MarketingNav({ t }) {
  return (
    <nav
      className="sticky top-0 z-40 backdrop-blur-lg bg-ink-bg/70 border-b border-white/[0.06]"
      data-testid="marketing-nav"
    >
      <div className="max-w-[1180px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/marketing" className="text-2xl font-black tracking-tight">Drivons</Link>
        <div className="hidden md:flex items-center gap-8 text-[13px] font-semibold text-white/70">
          <a href="#riders" className="hover:text-white">{t("mk.nav.riders", "Riders")}</a>
          <a href="#drivers" className="hover:text-white">{t("mk.nav.drivers", "Drivers")}</a>
          <a href="#services" className="hover:text-white">{t("mk.nav.services", "Services")}</a>
          <a href="#download" className="hover:text-white">{t("mk.nav.download", "Download")}</a>
        </div>
        <Link
          to="/login"
          className="pill-btn bg-white text-black rounded-full px-4 py-2 text-[13px] font-bold inline-flex items-center gap-1.5"
          data-testid="marketing-signin"
        >
          {t("mk.nav.signin", "Sign in")} <ChevronRight size={14} className="rtl-flip" />
        </Link>
      </div>
    </nav>
  );
}

function Hero({ t }) {
  return (
    <section className="relative overflow-hidden border-b border-white/[0.06]" data-testid="marketing-hero">
      <div className="absolute inset-0 -z-10">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1517596623376-deccebb96b46?w=1920&q=80)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-ink-bg via-ink-bg/85 to-transparent" />
      </div>
      <div className="max-w-[1180px] mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-[11px] font-semibold tracking-widest uppercase text-white/80">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-pulse" />
            {t("mk.hero.eyebrow", "Live in 6 languages")}
          </div>
          <h1 className="mt-6 text-5xl md:text-7xl font-black leading-[0.95] tracking-tight">
            {t("mk.hero.h1a", "Get anywhere.")}<br />
            <span className="text-white/45">{t("mk.hero.h1b", "Get anything done.")}</span>
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-xl leading-relaxed">
            {t("mk.hero.sub", "Rides, deliveries, moving, roadside help — Drivons connects you to a nearby, vetted operator in seconds.")}
          </p>
          <div className="mt-8 flex gap-3 flex-wrap">
            <a
              href="#download"
              className="pill-btn bg-white text-black rounded-full px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2"
              data-testid="hero-cta-download"
            >
              {t("mk.hero.ctaGet", "Get the app")} <ArrowRight size={16} className="rtl-flip" />
            </a>
            <a
              href="#drivers"
              className="pill-btn border border-white/25 text-white rounded-full px-6 py-3.5 text-sm font-bold hover:bg-white/5 inline-flex items-center gap-2"
              data-testid="hero-cta-drivers"
            >
              {t("mk.hero.ctaDrivers", "Drive with us")}
            </a>
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { k: "6",      v: t("mk.hero.stat1", "Languages") },
              { k: "8+",     v: t("mk.hero.stat2", "Service types") },
              { k: "24/7",   v: t("mk.hero.stat3", "Support") },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl md:text-4xl font-black tabular-nums">{s.k}</div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-white/50 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ServicesStrip({ t }) {
  return (
    <section id="services" className="border-b border-white/[0.06]" data-testid="marketing-services">
      <div className="max-w-[1180px] mx-auto px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-brand-accent">
            {t("mk.services.eyebrow", "One app, endless needs")}
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-black leading-tight tracking-tight">
            {t("mk.services.h2", "8 services, 1 tap away")}
          </h2>
          <p className="mt-4 text-white/60 leading-relaxed">
            {t("mk.services.sub", "From a quick ride to a mid-move breakdown — Drivons dispatches a nearby verified operator in seconds.")}
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CATEGORIES.map((c) => (
            <div
              key={c.id}
              className="rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/25 p-5 flex flex-col items-start gap-3 transition-colors"
              data-testid={`mk-cat-${c.id}`}
            >
              <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center">
                <c.icon size={20} strokeWidth={2.2} />
              </div>
              <div className="text-[15px] font-bold">{t(`categories.${c.id}`)}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function RidersDrivers({ t }) {
  return (
    <section id="riders" className="border-b border-white/[0.06]" data-testid="marketing-two-sides">
      <div className="max-w-[1180px] mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-6">
        {/* Riders */}
        <div className="rounded-3xl bg-white/[0.04] border border-white/10 p-8 md:p-10">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-brand-accent">
            {t("mk.riders.eyebrow", "For riders")}
          </div>
          <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tight leading-tight">
            {t("mk.riders.h3", "Book in seconds. Pay however you like.")}
          </h3>
          <ul className="mt-6 space-y-3 text-[15px] text-white/75">
            {["p1","p2","p3","p4"].map((k) => (
              <li key={k} className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" />
                <span>{t(`mk.riders.${k}`, "")}</span>
              </li>
            ))}
          </ul>
          <a href="#download" className="pill-btn mt-8 inline-flex items-center gap-2 bg-white text-black rounded-full px-5 py-3 text-[13px] font-bold">
            {t("mk.riders.cta", "Try Drivons free")} <ArrowRight size={14} className="rtl-flip" />
          </a>
        </div>
        {/* Drivers */}
        <div id="drivers" className="rounded-3xl border border-white/10 p-8 md:p-10 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, #0a2415 0%, #0f0f10 60%)" }}>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70">
            {t("mk.drivers.eyebrow", "FOR DRIVERS")}
          </div>
          <h3 className="mt-3 text-3xl md:text-4xl font-black tracking-tight leading-tight">
            {t("mk.drivers.h3", "Drive with us. Keep 85% of every ride.")}
          </h3>
          <ul className="mt-6 space-y-3 text-[15px] text-white/80">
            {["p1","p2","p3","p4"].map((k) => (
              <li key={k} className="flex items-start gap-3">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand-accent shrink-0" />
                <span>{t(`mk.drivers.${k}`, "")}</span>
              </li>
            ))}
          </ul>
          <Link to="/register?role=supplier" className="pill-btn mt-8 inline-flex items-center gap-2 bg-brand-accent text-black rounded-full px-5 py-3 text-[13px] font-bold" data-testid="mk-drivers-cta">
            {t("mk.drivers.cta", "Start earning")} <ArrowRight size={14} className="rtl-flip" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Features({ t }) {
  return (
    <section className="border-b border-white/[0.06]" data-testid="marketing-features">
      <div className="max-w-[1180px] mx-auto px-6 py-16 md:py-20">
        <div className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-brand-accent">
            {t("mk.features.eyebrow", "Why Drivons")}
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-black tracking-tight leading-tight">
            {t("mk.features.h2", "Fair pricing. Real drivers. No surprises.")}
          </h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-3">
          {FEATURES.map((f, i) => (
            <div key={i} className="rounded-2xl bg-white/[0.03] border border-white/[0.08] p-6 flex gap-4">
              <div className="w-11 h-11 shrink-0 rounded-full bg-white/10 flex items-center justify-center">
                <f.icon size={20} strokeWidth={2.2} />
              </div>
              <div>
                <div className="font-bold text-[16px]">{t(`mk.features.${f.kKey}`, "")}</div>
                <div className="text-[13px] text-white/60 mt-1 leading-relaxed">{t(`mk.features.${f.vKey}`, "")}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DownloadCta({ t }) {
  return (
    <section id="download" className="border-b border-white/[0.06]" data-testid="marketing-download">
      <div className="max-w-[1180px] mx-auto px-6 py-20 md:py-28 text-center">
        <div className="text-[11px] uppercase tracking-widest font-semibold text-brand-accent">
          {t("mk.dl.eyebrow", "Get Drivons")}
        </div>
        <h2 className="mt-3 text-4xl md:text-6xl font-black tracking-tight leading-[1.05]">
          {t("mk.dl.h2a", "Your city.")}<br />
          <span className="text-white/45">{t("mk.dl.h2b", "In one tap.")}</span>
        </h2>
        <p className="mt-6 text-white/60 max-w-xl mx-auto">
          {t("mk.dl.sub", "Available on iOS and Android. Free to download, no subscription.")}
        </p>
        <div className="mt-10 flex gap-4 justify-center flex-wrap">
          <StoreBadge href={APP_STORE_URL} store="apple" testid="badge-app-store" t={t} />
          <StoreBadge href={PLAY_STORE_URL} store="google" testid="badge-play-store" t={t} />
        </div>
        <div className="mt-6 text-[11px] text-white/40">
          {t("mk.dl.note", "Store links go live when the app is approved. Meanwhile, sign up at drivon.co.il.")}
        </div>
      </div>
    </section>
  );
}

function StoreBadge({ href, store, testid, t }) {
  const isApple = store === "apple";
  const line1 = isApple ? t("mk.dl.appleLine1", "Download on the") : t("mk.dl.googleLine1", "Get it on");
  const line2 = isApple ? "App Store" : "Google Play";
  return (
    <a
      href={href}
      data-testid={testid}
      className="pill-btn inline-flex items-center gap-3 rounded-2xl bg-white text-black px-5 py-3 min-w-[180px] hover:bg-white/90 transition-colors"
    >
      <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        {isApple ? (
          <path d="M17.05 13c0-2.9 2.34-4.24 2.45-4.31-1.34-2-3.42-2.23-4.16-2.26-1.77-.18-3.45 1.04-4.35 1.04-.9 0-2.29-1.02-3.76-.99-1.94.03-3.73 1.13-4.72 2.86-2.02 3.5-.52 8.68 1.45 11.52.96 1.38 2.11 2.94 3.62 2.88 1.45-.06 2-.94 3.77-.94 1.76 0 2.26.94 3.8.91 1.57-.03 2.56-1.4 3.52-2.79 1.11-1.6 1.57-3.16 1.6-3.25-.03-.02-3.07-1.18-3.09-4.67zM14.35 4.24c.8-.97 1.34-2.32 1.19-3.66-1.15.05-2.54.77-3.36 1.73-.74.85-1.39 2.22-1.22 3.53 1.28.1 2.6-.65 3.39-1.6z" />
        ) : (
          <path d="M3.4 21.6L14 11 3.4 0.4v21.2zm12.9-9.4l4.7-2.7-4.7-2.7-3.2 3.2 3.2 3.2zm-2 1.5L6.6 22h9L20 19.7l-5.7-6z" />
        )}
      </svg>
      <div className="text-start leading-tight">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{line1}</div>
        <div className="text-[15px] font-black">{line2}</div>
      </div>
    </a>
  );
}

function MarketingFooter({ t }) {
  return (
    <footer data-testid="marketing-footer" className="pb-24 md:pb-0">
      <div className="max-w-[1180px] mx-auto px-6 py-10 flex flex-wrap gap-4 items-center justify-between text-[12px] text-white/50">
        <div className="flex items-center gap-3">
          <span className="text-white font-bold text-lg">Drivons</span>
          <span>© {new Date().getFullYear()}</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-white">{t("footer.privacy", "Privacy")}</Link>
          <Link to="/terms" className="hover:text-white">{t("footer.terms", "Terms")}</Link>
          <Link to="/support" className="hover:text-white">{t("footer.support", "Support")}</Link>
        </div>
      </div>
    </footer>
  );
}
